import Storage from 'utils/localStorage/Storage';
import languageLocaleUtils from 'utils/LanguageLocale';
import sdnApi from 'ai/services/sdn';

const { getCurrentLanguageLocale } = languageLocaleUtils;

// GAI-7279: the SRP search terms returned by the entrypoint prompts service are
// cached locally for 24 hours. `Storage.local` auto-expires the whole entry once
// the TTL elapses; an in-memory copy avoids re-reading/parsing localStorage on
// every synchronous eligibility check during a page session.
const CACHE_TTL = Storage.DAYS * 1;

const KEYS = {
    GENAI_SRP_SEARCH_TERMS: 'genAiSrpSearchTerms'
};

/*
 * Storage format (single entry, keyed by locale):
 * {
 *   "en-US": ["body oil", "shampoo", ...],
 *   "fr-CA": ["huile visage", ...]
 * }
 */
const memoryCache = {};

const normalizeTerms = terms =>
    Array.isArray(terms)
        ? terms
            .filter(term => typeof term === 'string')
            .map(term => term.trim().toLowerCase())
            .filter(Boolean)
        : [];

const readFromStorage = () => {
    try {
        return Storage.local.getItem(KEYS.GENAI_SRP_SEARCH_TERMS) || null;
    } catch (error) {
        Sephora.logger.error('SuperChat: failed to read cached SRP search terms', error);

        return null;
    }
};

const setCachedSrpSearchTerms = (locale, terms) => {
    const normalized = normalizeTerms(terms);
    memoryCache[locale] = normalized;

    try {
        const stored = readFromStorage() || {};
        stored[locale] = normalized;
        Storage.local.setItem(KEYS.GENAI_SRP_SEARCH_TERMS, stored, CACHE_TTL);
    } catch (error) {
        Sephora.logger.error('SuperChat: failed to cache SRP search terms', error);
    }

    return normalized;
};

// Synchronous read used by the SRP eligibility check. Returns the cached, lowercased
// term list for the locale, or null when nothing valid is cached (or during SSR).
const getCachedSrpSearchTerms = (locale = getCurrentLanguageLocale()) => {
    if (Sephora.isNodeRender) {
        return null;
    }

    if (memoryCache[locale]) {
        return memoryCache[locale];
    }

    const stored = readFromStorage();
    const terms = stored?.[locale];

    // Guard against a corrupted/non-array cache entry so synchronous callers
    // (isAIBeautyChatEnabledSRP) never run .some()/.localeCompare() on a bad value.
    if (Array.isArray(terms)) {
        memoryCache[locale] = terms;

        return terms;
    }

    return null;
};

const fetchAndCacheSrpSearchTerms = (locale = getCurrentLanguageLocale()) => {
    return sdnApi
        .srpSearchTerms({ locale })
        .then(terms => setCachedSrpSearchTerms(locale, terms))
        .catch(error => {
            Sephora.logger.error('SuperChat: failed to fetch SRP search terms', error);

            return null;
        });
};

// Fire-and-forget prefetch triggered on app initialization. Respects the 24h cache:
// it only hits the network when there is no valid cached list for the current locale.
const prefetchSrpSearchTerms = () => {
    if (Sephora.isNodeRender) {
        return Promise.resolve(null);
    }

    const locale = getCurrentLanguageLocale();
    const cached = getCachedSrpSearchTerms(locale);

    if (cached) {
        return Promise.resolve(cached);
    }

    return fetchAndCacheSrpSearchTerms(locale);
};

export {
    getCachedSrpSearchTerms, setCachedSrpSearchTerms, fetchAndCacheSrpSearchTerms, prefetchSrpSearchTerms, KEYS
};
